import { Stack, Title } from '@mantine/core'
import type { Patient } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import type { IDT } from '@seen/core/models/care-teams/idt'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { SDRStatus } from '@seen/core/models/tasks/sdr/utils'
import { createReference } from '@seen/core/references'
import { type JSX, useState } from 'react'
import { Activity } from '#src/components/activity/Activity.tsx'
import { Card } from '#src/components/core/Card.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { SDRAdvanceToInReview } from '#src/components/sdr/display/SDRAdvanceToInReview.tsx'
import { SDRDisplayForwardBackButtons } from '#src/components/sdr/display/SDRDisplayForwardBackButtons.tsx'
import { SDRDispositionResult } from '#src/components/sdr/display/SDRDispositionResult.tsx'
import { SDRFulfillmentUpdatesTable } from '#src/components/sdr/display/SDRFulfillmentUpdatesTable.tsx'
import { SDRIDTReview } from '#src/components/sdr/display/SDRIDTReview.tsx'
import { SDRIDTReviewResult } from '#src/components/sdr/display/SDRIDTReviewResult.tsx'
import { SDRMarkNotified } from '#src/components/sdr/display/SDRMarkNotified.tsx'
import { SDRNotificationAttemptsTable } from '#src/components/sdr/display/SDRNotificationAttemptsTable.tsx'
import { SDRProgress } from '#src/components/sdr/display/SDRProgress.tsx'
import { SDRRequestDetails } from '#src/components/sdr/display/SDRRequestDetails.tsx'
import { SDRRequesterContactInfo } from '#src/components/sdr/display/SDRRequesterContactInfo.tsx'
import { SDRSetDisposition } from '#src/components/sdr/display/SDRSetDisposition.tsx'
import { SDRSignatures } from '#src/components/sdr/display/SDRSignatures.tsx'
import { SDRTasks } from '#src/components/sdr/display/SDRTasks.tsx'
import { SDRUpdateCarePlan } from '#src/components/sdr/display/SDRUpdateCarePlan.tsx'
import type { SDRDisplayStep } from '#src/components/sdr/display/utils.ts'
import { SDRActivity } from '#src/pages/task/SDRActivity.tsx'

type Props = {
  patient: Patient
  sdr: SDR
  careTeam: IDT
  onClickNavigate: (taskId?: string) => void
}

export function SDRDisplay({ sdr, patient, careTeam }: Props): JSX.Element {
  invariant(sdr.id, 'SDR must have an ID')

  const sdrStatus = sdr.status
  let maxStep: SDRDisplayStep
  switch (sdrStatus) {
    case SDRStatus.REQUESTED:
    case null:
      maxStep = 1
      break
    case SDRStatus.IN_REVIEW:
      maxStep = 2
      break
    case SDRStatus.APPROVED:
    case SDRStatus.PARTIALLY_APPROVED:
    case SDRStatus.DENIED:
    case SDRStatus.PARTIALLY_DENIED:
    case SDRStatus.IN_APPEAL:
    case SDRStatus.WITHDRAWN:
      maxStep = sdr.notificationDate ? 4 : 3
      break
  }

  const [_selectedStep, setSelectedStep] = useState<SDRDisplayStep | null>(null)
  const selectedStep = _selectedStep ?? maxStep

  let selectedStepBody: JSX.Element | null = null
  switch (selectedStep) {
    case 1:
      selectedStepBody = (
        <>
          <Card>
            <SDRRequestDetails patient={patient} sdr={sdr} />
          </Card>

          <Card>
            <SDRAdvanceToInReview
              sdr={sdr}
              selectedStep={selectedStep}
              setSelectedStep={setSelectedStep}
            />
          </Card>
        </>
      )
      break
    case 2:
      selectedStepBody = (
        <>
          <Card>
            <SDRIDTReview sdr={sdr} />
          </Card>

          <Card className="flex flex-col gap-3">
            <h2>Assessment</h2>
            <ResourceFieldForm
              instance={sdr}
              valueFn={(sdr) => sdr.assessment}
              patchFn={(sdr, value) => {
                sdr.assessment = value ?? undefined
              }}
              debounceTyping
              render={({ field }) => (
                <RichTextEditor
                  variant="unstyled"
                  placeholder="Add assessment…"
                  {...field}
                />
              )}
            />
          </Card>

          <Card>
            <SDRTasks sdr={sdr} />
          </Card>

          <Card>
            <SDRSetDisposition sdr={sdr} careTeam={careTeam} />
          </Card>

          <Card>
            <SDRSignatures sdr={sdr} careTeam={careTeam} />
          </Card>

          {sdrStatus !== SDRStatus.IN_REVIEW && (
            <Card>
              <SDRDisplayForwardBackButtons
                selectedStep={selectedStep}
                setSelectedStep={setSelectedStep}
              />
            </Card>
          )}
        </>
      )
      break
    case 3:
      selectedStepBody = (
        <>
          <Card>
            <SDRDispositionResult sdr={sdr} />
          </Card>

          <Card>
            <SDRRequesterContactInfo sdr={sdr} patient={patient} />
          </Card>

          <Card>
            <SDRNotificationAttemptsTable sdr={sdr} patient={patient} />
          </Card>

          <Card>
            <SDRMarkNotified
              sdr={sdr}
              selectedStep={selectedStep}
              setSelectedStep={setSelectedStep}
            />
          </Card>
        </>
      )
      break
    case 4:
      selectedStepBody = (
        <>
          <Card>
            <SDRRequestDetails patient={patient} sdr={sdr} />
          </Card>

          <Card>
            <SDRIDTReviewResult sdr={sdr} />
          </Card>

          <Card>
            <SDRDispositionResult sdr={sdr} />
          </Card>

          <Card>
            <SDRUpdateCarePlan />
          </Card>

          <Card>
            <SDRFulfillmentUpdatesTable
              patientRef={createReference(patient)}
              sdr={sdr}
            />
          </Card>
        </>
      )
      break
  }

  return (
    <Stack className="pb-10">
      <Title order={2} className="relative">
        SDR: {sdr.title}
        <DevMedplumAppLinkOverlay to={sdr} />
      </Title>
      <Card>
        <SDRProgress
          sdr={sdr}
          patient={patient}
          stepProps={{
            maxStep,
            selectedStep: selectedStep ?? maxStep,
            setSelectedStep,
          }}
        />
      </Card>

      {selectedStepBody}

      <Card>
        <Activity resource={sdr} subject={sdr.for}>
          <SDRActivity sdr={sdr} />
        </Activity>
      </Card>
    </Stack>
  )
}
